// Real HTTP adapter for Biometric Data (PRD 09 / FEAT-032).
// Maps the server error envelope { error: { code, message, fields } } to BiometricApiError.
// Endpoints:
//   GET /admin/biometric-ranges            → { data: [ { metric, warnLow, normalMin, normalMax, warnHigh } x3 ] }
//   PUT /admin/biometric-ranges/{METRIC}   → { data: { metric, warnLow, normalMin, normalMax, warnHigh } }
// The BE keys each row by the UPPERCASE metric code; the FE public types use the lowercase
// BiometricKey. We translate only here, at the wire boundary — the BiometricApi shape is unchanged.
// Activated by setting VITE_USE_MOCK_API=false; see biometric.adapter.ts.

import { endpoints } from '@/api/endpoints';
import { httpClient } from '@/api/http-client';
import {
  BiometricApiError,
  type BiometricApi,
  type BiometricApiErrorCode,
  type BiometricKey,
  type BiometricRange,
  type BiometricRangesResponse,
  type BiometricRangeUpdateInput,
} from '../types/biometric.types';

// BE metric codes (UPPERCASE). Kept local to the wire boundary.
// HEART_RATE (안정시 심박수) replaced BMI per PRD 09 v1.4; kept ≤16 chars for the DB metric column.
type BiometricMetric = 'STEP_COUNT' | 'SLEEP_DURATION' | 'HEART_RATE';

const KEY_TO_METRIC: Record<BiometricKey, BiometricMetric> = {
  steps: 'STEP_COUNT',
  sleep: 'SLEEP_DURATION',
  heartRate: 'HEART_RATE',
};

const METRIC_TO_KEY: Record<BiometricMetric, BiometricKey> = {
  STEP_COUNT: 'steps',
  SLEEP_DURATION: 'sleep',
  HEART_RATE: 'heartRate',
};

// One row of the BE payload: the metric code plus the four numeric thresholds.
interface BiometricRangeItem extends BiometricRange {
  metric: BiometricMetric;
}

interface ApiEnvelope<T> {
  data: T;
}

interface ServerErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string>;
  };
}

function toRange(item: BiometricRangeItem): BiometricRange {
  return {
    warnLow: item.warnLow,
    normalMin: item.normalMin,
    normalMax: item.normalMax,
    warnHigh: item.warnHigh,
  };
}

function mapError(e: unknown): never {
  const candidate = e as { response?: { data?: ServerErrorEnvelope } };
  const payload = candidate.response?.data?.error;
  throw new BiometricApiError(
    (payload?.code as BiometricApiErrorCode) ?? 'UNKNOWN',
    payload?.message ?? '알 수 없는 오류가 발생했습니다.',
    payload?.fields,
  );
}

export const biometricHttpApi: BiometricApi = {
  async getRanges(): Promise<BiometricRangesResponse> {
    try {
      const res = await httpClient.get<ApiEnvelope<BiometricRangeItem[]>>(
        endpoints.biometricRanges,
      );
      const data = res.data?.data;
      // A non-array body (wrong envelope, paginated wrapper, SPA fallback HTML)
      // would make `.reduce` throw a raw TypeError — surface a clean load error.
      if (!Array.isArray(data)) {
        throw new BiometricApiError('UNKNOWN', '생체 데이터 기준을 불러올 수 없습니다.');
      }
      // Reduce the BE array into the FE's Record keyed by the lowercase BiometricKey.
      return data.reduce((acc, item) => {
        const key = METRIC_TO_KEY[item.metric];
        if (key) acc[key] = toRange(item);
        return acc;
      }, {} as BiometricRangesResponse);
    } catch (e) {
      mapError(e);
    }
  },

  async updateRange({
    key,
    range,
  }: BiometricRangeUpdateInput): Promise<BiometricRange> {
    try {
      const metric = KEY_TO_METRIC[key];
      const res = await httpClient.put<ApiEnvelope<BiometricRangeItem>>(
        `${endpoints.biometricRanges}/${metric}`,
        range,
      );
      return toRange(res.data.data);
    } catch (e) {
      mapError(e);
    }
  },
};
